import React, {Dispatch, useEffect, useMemo, useState} from 'react';
import {
  ActionType,
  AssetMetaInterface,
  AssetService,
  ComputedPageItem,
} from '@taopix/taopix-core/dist/types/types';
import {useTranslation} from 'react-i18next';
import {
  Alert,
  Button,
  ContentPanel,
  Icon,
  PictureIcon,
  Theme,
  ThemeName,
  TrashIcon,
} from '@taopix/taopix-design-system';
import AddPicturesButton, {PictureSortType} from '../../AssetServices/AddPicturesButton';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import {AssetPart, AssetStatus, CoreActions} from '@taopix/taopix-core';
import {useDispatch} from 'react-redux';
import {Feedback} from '../../../types';
import {useAssetServices} from '../../Context/AssetServices/AssetServicesContext';
import {COLOUR_COMPONENT_FLATTENED} from '../../../Constants';
import useMetaDataChecker from '../../../Hooks/AssetService/useMetaDataChecker';
import useStatusChecker from '../../../Hooks/Assets/useStatusChecker';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import MediaGallery from '../../MediaGallery/MediaGallery';
import ToolPaletteSection from '../../ToolPalette/ToolPaletteSection';
import useSettings from '../../../Hooks/useSettings';
import classNames from 'classnames';
import {AssetServicesActions} from '../../Context/AssetServices/AssetServicesActions';
import {calcRotation} from '../../../Utils/Asset';
import {useExperience} from '../../Experience/ExperienceProvider';

export type PicturesProps = {
  assetServices?: AssetService[];
  sectionKey: string;
  item?: ComputedPageItem;
  assets: AssetMetaInterface[];
  server: APIServer;
  children?: any;
  buttonLabel?: string;
  onSelectPicture: (asset: any) => void;
  onRemovePicture: () => void;
  selectedAssetId?: string | number;
  onItemDragStart?: (event: React.DragEvent<HTMLElement>, asset: any) => void;
  onItemDragEnd?: (event: React.DragEvent<HTMLElement>, asset: any) => void;
  onItemTouchStart?: (event: React.TouchEvent<HTMLElement>) => void;
  onItemTouchEnd?: (event: React.TouchEvent<HTMLElement>) => void;
};

const CalendarPicturePalette = React.forwardRef(
  (
    {
      sectionKey,
      assetServices,
      children,
      server,
      assets,
      item,
      onItemDragStart,
      onItemDragEnd,
      onItemTouchStart,
      onItemTouchEnd,
      buttonLabel,
      onSelectPicture,
      onRemovePicture,
      selectedAssetId,
      ...props
    }: PicturesProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const ui = useUiState();
    const settings = useSettings();
    const assetServiceContext = useAssetServices();
    const metaDataChecker = useMetaDataChecker();
    const monitor = useStatusChecker();
    const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();
    const {t} = useTranslation();
    const {experience} = useExperience();

    const [sortType] = useState<PictureSortType>(PictureSortType.Filename);
    // const [thumbnailStyle, setThumbnailStyle] = useState<ThumbnailStyle>('proportional');
    const [hideUsed] = useState<boolean>(false);

    const [feedback, setFeedback] = useState<Feedback>({
      type: 'info',
      message: '',
      open: false,
      showHeader: true,
    });

    const stateManager = window.Taopix.Core.get('stateManager');
    const layout = stateManager.getLayout();

    const pictureUsage = Array.isArray(layout?.usagepicture) ? {} : layout?.usagepicture;

    useEffect(() => {
      monitor?.on('update', event => {
        const updates = event.detail;
        // update each of the assets
        updates.forEach(update => {
          const asset = assets.find(a => a.id === update.id);
          if (!asset) {
            return;
          }

          // get a flattened FPO to use for the thumbnail
          const thumbsrc =
            update?.children?.find(
              (c: any) =>
                c &&
                c?.part === AssetPart.Intermediate &&
                c?.colourComponent === COLOUR_COMPONENT_FLATTENED
            )?.src || '';

          // update the asset
          dispatch(
            CoreActions.updateAsset({
              ...asset,
              ...(update as unknown as AssetMetaInterface),
              src: thumbsrc,
              thumbnailsrc: thumbsrc,
              hiressrc: thumbsrc,
            })
          );
        });
      });

      assets.forEach(asset => {
        if (asset.status === AssetStatus.Uploaded) {
          monitor?.add(asset.id);
        }
      });
    }, [assets, dispatch]);

    // check the meta data of any library link assets
    useEffect(() => {
      // if we have no assets, then do nothing
      if (!assets.length) {
        return;
      }
      metaDataChecker?.checkMeta(assets as any).then(response => {
        response.forEach(link => {
          const asset = assets.find(a => a?.link?.id === link.id);
          if (!asset) {
            return;
          }

          dispatch(
            CoreActions.updateAsset({
              ...asset,
              link: {
                ...asset.link,
                ...link,
              },
            })
          );
        });
      });
    }, [metaDataChecker]);

    const label = t('str_LabelPictures') + ' (' + assets.length + ')';

    const pictureListClasses = classNames(
      'px-md',
      'mt-md',
      ui.state.isSmallScreen ? 'overflow-y-auto' : 'overflow-x-auto'
    );

    const onToggleServiceList = (anchor: string, useParentWidth: boolean) => {
      assetServiceContext.dispatch(
        AssetServicesActions.setOpenState({
          open: !assetServiceContext.state.open,
          anchor: anchor,
          useParentWidth: useParentWidth,
        })
      );
    };

    const pictureButtonId = 'tds-calendar-picture-palette-add-pictures';

    // send user back to previous palette if picture is selected
    useEffect(() => {
      if (selectedAssetId !== '') {
        ui.dispatch(
          UserInterfaceActions.setToolPaletteSection('calendar-palette/cell-editor-palette')
        );
      }
    }, [selectedAssetId]);

    // pass in selected asset id, search for asset to populate buttonIcon
    const [selectedAsset, setSelectedAsset] = useState<AssetMetaInterface>();
    useEffect(() => {
      setSelectedAsset(Object.values(assets).find(asset => asset?.assetRef === selectedAssetId));
    }, [selectedAssetId]);

    const rotation = useMemo(() => {
      if (selectedAsset) return calcRotation(selectedAsset);
      return 0;
    }, [selectedAsset]);

    return (
      <ContentPanel className={'flex p-sm'}>
        <div className={'flex flex-1 items-center'}>
          <p>{buttonLabel}</p>
          {selectedAssetId && (
            <Button
              startIcon={<TrashIcon />}
              label={t('*:str_LabelRemovePicture')}
              hideLabel
              buttonStyle={'standard'}
              className={'ml-auto mr-xs'}
              onClick={() => onRemovePicture()}
            />
          )}
        </div>
        <ToolPaletteSection
          id={'calendar-pictures-tool-palette'}
          sectionKey={sectionKey}
          smallScreenPanelHeight="full"
          title={t('str_LabelPictures')}
          buttonLabel={label}
          buttonIcon={
            selectedAssetId === '' ? (
              <PictureIcon />
            ) : (
              <img
                src={selectedAsset?.src}
                className={'lg:w-[24px] lg:h-[24px] sm:w-[26px] sm:h-[26px] object-cover'}
                alt={'selected'}
                style={{transform: `rotate(${rotation}deg)`}}
              ></img>
            )
          }
          subSection={true}
          buttonLayout={'icon-only'}
          ref={ref}
          parentselected={true}
        >
          {ui.state.palette.section === `calendar-palette/cell-editor-palette/${sectionKey}` ? (
            <div className={'flex flex-col flex-1 min-h-0'}>
              <div
                className={
                  'flex px-md lg:flex-col sm:self-center sm:w-[300px] sm:max-w-[calc(100%_-_100px)] sm:space-x-xs lg:space-y-sm sm:mb-sm'
                }
              >
                <AddPicturesButton
                  popoutUsesParentWidth
                  buttonProps={{
                    id: pictureButtonId,
                    size:
                      experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuc' &&
                      ui.state.isSmallScreen
                        ? 'medium'
                        : 'small',
                    buttonStyle: ui.state.isSmallScreen ? 'primary' : 'secondary',
                    startIcon: undefined,
                    className: 'sm:flex-1',
                    arrow: !ui.state.isSmallScreen ? 'down' : undefined,
                  }}
                  popoutProps={{
                    className: 'sm:min-w-[min(theme(width.bounds),300rem)]',
                    popDirection: ui.state.isSmallScreen ? 'up' : 'down',
                  }}
                />
              </div>

              {assets.length === 0 ? (
                <Theme
                  name={ThemeName.Container}
                  className={'flex mx-md flex-1 justify-center items-center p-md my-md text-center'}
                >
                  <p>{t('str_MessageClickAddPictures')}</p>
                </Theme>
              ) : (
                <>
                  <div id={'calendar-toolPalettePictureList'} className={pictureListClasses}>
                    <MediaGallery
                      showUsage
                      onItemDragStart={onItemDragStart}
                      onItemDragEnd={onItemDragEnd}
                      onItemTouchStart={onItemTouchStart}
                      onItemTouchEnd={onItemTouchStart}
                      hideUsed={hideUsed}
                      pictureUsage={pictureUsage}
                      showPreviewButton={false}
                      thumbnailLayout={'full-frame-tiles'}
                      sortBy={sortType}
                      sortDirection={ui.state.palette.picturePallette.sortDirection}
                      imageList={assets as any}
                      onSelectPicture={(checked, asset) => {
                        if (onSelectPicture) {
                          onSelectPicture(asset);
                        }
                      }}
                      selectableImages={true}
                      selectionAppearance={'border'}
                      groupedView={true}
                      detailsPopoutAnchorId={'calendar-toolPalettePictureList'}
                      thumbnailSize={100}
                      headingSize={ui.state.isSmallScreen ? 5 : 3}
                      showHeadings={ui.state.isSmallScreen ? false : true}
                      localeString={localStorage.getItem('i18nextLng') ?? 'en'}
                      isSmallScreen={ui.state.isSmallScreen}
                      currencyCode={settings.state.currency.code}
                      showDeleteButton={false}
                    />
                  </div>
                  <Alert
                    id={`calendar-error-${feedback.type}`}
                    open={feedback.open}
                    type={feedback.type}
                    message={feedback.message}
                    onClose={() => {
                      setFeedback(current => {
                        return {
                          ...current,
                          open: false,
                        };
                      });
                    }}
                  />
                </>
              )}
            </div>
          ) : (
            <></>
          )}
        </ToolPaletteSection>
      </ContentPanel>
    );
  }
);

export default CalendarPicturePalette;
